/**
 * useDataGridProps Hook
 *
 * Transforms raw props from DataTablePanel into the format expected by the DataGrid React component.
 * This hook replaces the prop-building logic from visualizer/js/modules/core/dataGrid/dataGrid.component.js
 */

import { useMemo, useCallback } from "react";
import {
  getFormatters,
  transformRowsForDataGrid,
  tableConfigFieldObjsToColumnConfigs,
  getColumnHeaderOffset,
} from "../utils/dataGridTransformers";

/**
 * Hook to build DataGrid props from DataTablePanel data
 *
 * @param {Object} params - Input parameters
 * @param {Array} params.data - Raw row data from API
 * @param {Array} params.fields - Field model objects
 * @param {string} params.fieldFormatIdentifier - Field format identifier for conditional formatting
 * @param {Array} params.tableConfigFieldObjs - Table configuration field objects
 * @param {Object} params.commentCounts - Comment counts by row
 * @param {Array} params.checkedRecordIds - Array of checked record IDs
 * @param {boolean} params.rowCheckedInverse - Whether row selection is inversed
 * @param {number|string} params.selectedRecordId - Currently selected record ID
 * @param {boolean} params.bulkModeEnabled - Whether bulk mode is enabled
 * @param {Array} params.filterFieldNames - Field names that are filtered
 * @param {number} params.filteredRecordCount - Count of filtered records
 * @param {number} params.fixedColumnsLeft - Number of fixed columns on left
 * @param {Function} params.getAttachmentUrl - Function to get attachment URL
 * @param {boolean} params.highlightSelectedRow - Whether to highlight selected row
 * @param {boolean} params.isHeaderClickingEnabled - Whether header clicking is enabled
 * @param {boolean} params.isHeaderIconEnabled - Whether header icons are enabled
 * @param {boolean} params.isRowSelectionEnabled - Whether row selection is enabled
 * @param {string} params.selectedFieldName - Currently selected field name
 * @param {string} params.sortFieldName - Field name for sorting
 * @param {string} params.sortOrder - Sort order (asc/desc)
 * @param {boolean} params.showCommentCount - Whether to show comment counts
 * @param {Object} params.appConfig - Application configuration with feature flags
 * @param {Function} params.onColumnHeaderClick - Callback for column header click
 * @param {Function} params.onColumnResize - Callback for column resize
 * @param {Function} params.onRowClick - Callback for row click
 * @param {Function} params.onRowDoubleClick - Callback for row double click
 * @param {Function} params.onScrollToBottom - Callback for scroll to bottom
 * @param {Function} params.onSelectionChange - Callback for selection change
 * @param {number} params.conditionalFormattingVersion - Version counter to trigger re-computation when conditional formatting changes
 * @returns {Object} - Props ready for DataGrid component
 */
export function useDataGridProps({
  data,
  fields,
  fieldFormatIdentifier,
  tableConfigFieldObjs,
  commentCounts,
  checkedRecordIds,
  rowCheckedInverse,
  selectedRecordId,
  bulkModeEnabled,
  filterFieldNames,
  filteredRecordCount,
  fixedColumnsLeft,
  getAttachmentUrl,
  highlightSelectedRow,
  isHeaderClickingEnabled = true,
  isHeaderIconEnabled,
  isRowSelectionEnabled,
  selectedFieldName,
  sortFieldName,
  sortOrder,
  showCommentCount,
  appConfig,
  onColumnHeaderClick,
  onColumnResize,
  onRowClick,
  onRowDoubleClick,
  onScrollToBottom,
  onSelectionChange,
  conditionalFormattingVersion = 0,
}) {
  // Memoize formatters - recalculate when fields, fieldFormatIdentifier, or conditionalFormattingVersion changes
  const formatters = useMemo(
    () => getFormatters(fields, fieldFormatIdentifier),
    [fields, fieldFormatIdentifier, conditionalFormattingVersion]
  );

  // Memoize transformed data - recalculate when raw data, fields, formatters, getAttachmentUrl, or conditionalFormattingVersion changes
  const transformedData = useMemo(
    () => transformRowsForDataGrid(data, fields, formatters, getAttachmentUrl),
    [data, fields, formatters, getAttachmentUrl, conditionalFormattingVersion]
  );

  // Memoize column configs - only recalculate when tableConfigFieldObjs changes
  const columnConfigs = useMemo(
    () => tableConfigFieldObjsToColumnConfigs(tableConfigFieldObjs),
    [tableConfigFieldObjs]
  );

  // Wrap column header click to add offset calculation
  const handleColumnHeaderClick = useCallback(
    (columnIndex, fieldName, mouseEvent) => {
      if (onColumnHeaderClick) {
        const offset = getColumnHeaderOffset(mouseEvent);
        onColumnHeaderClick({
          columnIndex,
          fieldName,
          mouseEvent,
          offset,
        });
      }
    },
    [onColumnHeaderClick]
  );

  // Wrap column resize callback
  const handleColumnResize = useCallback(
    (columnIndex, fieldName, width) => {
      if (onColumnResize) {
        onColumnResize({ columnIndex, fieldName, width });
      }
    },
    [onColumnResize]
  );

  // Wrap row click callback
  const handleRowClick = useCallback(
    (row, rowId) => {
      if (onRowClick) {
        onRowClick({ row, rowId });
      }
    },
    [onRowClick]
  );

  // Wrap row double click callback
  const handleRowDoubleClick = useCallback(
    (row, rowId) => {
      if (onRowDoubleClick) {
        onRowDoubleClick({ row, rowId });
      }
    },
    [onRowDoubleClick]
  );

  // Wrap scroll to bottom callback
  const handleScrollToBottom = useCallback(() => {
    if (onScrollToBottom) {
      onScrollToBottom();
    }
  }, [onScrollToBottom]);

  // Wrap selection change callback
  const handleSelectionChange = useCallback(
    (checkedRowIds, selectedRowId, inverseMode) => {
      if (onSelectionChange) {
        onSelectionChange({ checkedRowIds, selectedRowId, inverseMode });
      }
    },
    [onSelectionChange]
  );

  // Build final props object - memoized to prevent unnecessary re-renders
  const dataGridProps = useMemo(() => {
    // Feature flags from appConfig
    const features = (appConfig && appConfig.features) || {};

    return {
      // Data
      data: transformedData,
      fields,
      fieldFormatIdentifier,
      columnConfigs,

      // Row identification
      rowIdKey: "metadata.exception_id",
      positionKey: "metadata.position",

      // Selection state
      checkedRowIds: checkedRecordIds,
      rowSelectInverse: rowCheckedInverse,
      selectedRowId: selectedRecordId,
      showCheckedRowsOnly: bulkModeEnabled,

      // Filtering & sorting
      filterFieldNames,
      filteredRecordCount,
      sortFieldName,
      sortOrder,
      selectedFieldName,

      // Layout
      fixedColumnsLeft,
      highlightSelectedRow,

      // Feature flags
      isHeaderIconEnabled,
      isRowSelectionEnabled,
      detectScrollToBottomByHeight: !!features.detectScrollToBottomByHeight,
      isPositionEnabled: !!features.remediationColumns,
      isCommentsEnabled: !!features.remediationColumns && showCommentCount,

      // Comments
      commentCounts,

      // Attachment handling
      getAttachmentUrl,

      // Event handlers
      onColumnHeaderClick: isHeaderClickingEnabled ? handleColumnHeaderClick : null,
      onColumnResize: handleColumnResize,
      onRowClick: handleRowClick,
      onRowDoubleClick: handleRowDoubleClick,
      onScrollToBottom: handleScrollToBottom,
      onSelectionChange: handleSelectionChange,
    };
  }, [
    transformedData,
    fields,
    fieldFormatIdentifier,
    columnConfigs,
    checkedRecordIds,
    rowCheckedInverse,
    selectedRecordId,
    bulkModeEnabled,
    filterFieldNames,
    filteredRecordCount,
    sortFieldName,
    sortOrder,
    selectedFieldName,
    fixedColumnsLeft,
    highlightSelectedRow,
    isHeaderIconEnabled,
    isRowSelectionEnabled,
    showCommentCount,
    commentCounts,
    getAttachmentUrl,
    appConfig,
    isHeaderClickingEnabled,
    handleColumnHeaderClick,
    handleColumnResize,
    handleRowClick,
    handleRowDoubleClick,
    handleScrollToBottom,
    handleSelectionChange,
  ]);

  return dataGridProps;
}

export default useDataGridProps;

